import React, { useEffect, useState } from "react";
import { DatePicker, Spin, Tag } from "antd";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { ICustomerTripStats } from "../../../models/seat";
import { getStatisticCustomerApi } from "../../../apis/customer.api";

dayjs.extend(utc);

interface CustomerStatisticComponentProps {
  id: string;
}

const CustomerStatisticComponent: React.FC<CustomerStatisticComponentProps> = ({
  id,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ICustomerTripStats | null>(null);

  // ngày mặc định = hôm nay
  const [date, setDate] = useState<Dayjs>(dayjs());

  const handleFetchStatistic = async (d: Dayjs) => {
    if (!id) return;

    setLoading(true);
    try {
      const dateStr = d.utc().startOf("day").toISOString(); // convert đúng utc

      const res = await getStatisticCustomerApi(id, dateStr);

      if (res?.data) setData(res.data);
      else setData(null);
    } catch (error) {
      console.error("Error fetching customer statistic:", error);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  // gọi API lần đầu
  useEffect(() => {
    handleFetchStatistic(date);
  }, [id]);

  // khi đổi ngày
  const onDateChange = (d: Dayjs | null) => {
    if (!d) return;
    setDate(d);
    handleFetchStatistic(d);
  };

  return (
    <div className="bg-white shadow-sm border border-gray-200 rounded-2xl p-6 mt-6">
      <div className="w-full flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-800 ">Thống kê</h2>
        <DatePicker
          value={date}
          onChange={onDateChange}
          format="DD-MM-YYYY"
          size="middle"
          className=" sm:w-[100px]"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Spin />
        </div>
      ) : !data ? (
        <p className="text-gray-500 italic">Không có dữ liệu thống kê.</p>
      ) : (
        <>
          {/* Tổng chuyến */}
          <div className="bg-gradient-to-br from-blue-500 mb-3 to-blue-600 rounded-2xl p-5 shadow-md text-white">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-lg font-medium opacity-90">Tổng số chuyến đi</p>
                <p className="text-4xl font-extrabold mt-1 text-center">
                  {+data?.totalTrips}
                </p>
              </div>

              {/* Icon làm đẹp góc phải */}
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-7 w-7 text-white"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 2a10 10 0 1010 10A10.011 10.011 0 0012 2zm1 15h-2v-2h2zm0-4h-2V7h2z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Danh sách chuyến */}
          <div className="space-y-4">
            {data.trips.map((t) => (
              <div
                key={t._id}
                className="p-3 rounded-2xl border border-gray-200 bg-white shadow-md"
              >
                {/* Tiêu đề */}
                <div className="flex flex-col justify-between items-start mb-2 gap-2">
                  <h3 className="text-xl font-medium text-gray-900 leading-snug">
                    {t.tripDetail.name}
                  </h3>
                </div>

                <div className="mb-3 flex flex-col gap-2 mt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 ">
                      Ngày khởi hành:
                    </span>
                    <span className="text-base font-medium text-gray-900">
                      {dayjs(t.tripDetail.departureDate).format("DD-MM-YYYY")}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 ">Ngày âm:</span>
                    <span className="text-base  text-gray-900 font-medium">
                      {dayjs(t.tripDetail.departureLunaDate).format(
                        "DD-MM-YYYY"
                      )}
                    </span>
                  </div>{" "}
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 ">Thời lượng:</span>
                    <span className="text-base font-medium text-gray-900">
                      {t.tripDetail.numberOfDate} ngày •{" "}
                      {t.tripDetail.numberOfNight} đêm
                    </span>
                  </div>{" "}
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 ">Vé đã đặt:</span>
                    <span className="text-lg font-medium text-red-500">
                      {t.seatCount}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 ">
                      Danh sách ghế:
                    </span>
                    <div className="text-base font-medium flex flex-wrap gap-2">
                      {t.seats.map((code) => (
                        <span
                          key={code}
                          className="text-lg font-bold text-blue-600"
                        >
                          {code}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default CustomerStatisticComponent;
